import { Box, Tooltip } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { getExceptionalStatusTranslationKey } from "src/utils/getExceptionalStatusTranslationKey";
import { memo, useMemo } from "react";
import { LocationExceptionalStatusRead } from "src/store/api/generatedApi";
import { TimelineSlot } from "./timelineLayout";

dayjs.extend(utc);

interface ExceptionalStatusMarkersProps {
  exceptionalStatuses: LocationExceptionalStatusRead[];
  slot: TimelineSlot;
  getPositionPercent: (time: Dayjs) => number;
  suppressTooltip?: boolean;
}

export const ExceptionalStatusMarkers = memo(
  ({
    exceptionalStatuses,
    slot,
    getPositionPercent,
    suppressTooltip = false,
  }: ExceptionalStatusMarkersProps) => {
    return (
      <>
        {exceptionalStatuses.map((status) => (
          <ExceptionalStatusMarker
            key={status.id}
            status={status}
            slot={slot}
            getPositionPercent={getPositionPercent}
            suppressTooltip={suppressTooltip}
          />
        ))}
      </>
    );
  }
);
ExceptionalStatusMarkers.displayName = "ExceptionalStatusMarkers";

export const EXCEPTIONAL_STATUS_STYLES = {
  missing: { bgcolor: "#000000", opacity: 0.5 },
  ignorable: { bgcolor: "grey.500", opacity: 0.4 },
  bad_labels: { bgcolor: "warning.main", opacity: 0.4 },
  // Area-targeted; never reaches this location-keyed timeline. Listed so
  // EXCEPTIONAL_STATUS_STYLES[status.status] stays exhaustive over the enum.
  broken_scale_weights: { bgcolor: "warning.main", opacity: 0.4 },
} as const;

const ExceptionalStatusMarker = memo(
  ({
    status,
    slot,
    getPositionPercent,
    suppressTooltip,
  }: {
    status: LocationExceptionalStatusRead;
    slot: TimelineSlot;
    getPositionPercent: (time: Dayjs) => number;
    suppressTooltip: boolean;
  }) => {
    const statusStart = dayjs.utc(status.started_at);
    const statusEnd = dayjs.utc(status.ended_at);

    const rawLeft = getPositionPercent(statusStart);
    const leftPercent = Math.max(0, rawLeft);
    const widthPercent = Math.min(
      100 - leftPercent,
      getPositionPercent(statusEnd) - leftPercent
    );

    const style = EXCEPTIONAL_STATUS_STYLES[status.status];
    const { t } = useTenantTranslation();

    const tooltipTitle = useMemo(() => {
      const start = dayjs.utc(status.started_at).format("YYYY-MM-DD HH:mm");
      const end = dayjs.utc(status.ended_at).format("YYYY-MM-DD HH:mm");
      const range = `${start} – ${end} UTC`;
      const statusLabel = t(getExceptionalStatusTranslationKey(status.status));
      const detail = status.note
        ? `${statusLabel}: ${status.note}`
        : statusLabel;
      return `${range}\n${detail}`;
    }, [status.started_at, status.ended_at, status.note, status.status, t]);

    const marker = (
      <Box
        sx={{
          position: "absolute",
          left: `${leftPercent}%`,
          width: `${Math.max(widthPercent, 0.5)}%`,
          top: slot.top,
          height: slot.height,
          bgcolor: style.bgcolor,
          opacity: style.opacity,
          borderRadius: slot.borderRadius,
          cursor: "inherit",
          ...(suppressTooltip ? { pointerEvents: "none" } : {}),
        }}
      />
    );

    return suppressTooltip ? (
      marker
    ) : (
      <Tooltip title={tooltipTitle} arrow>
        {marker}
      </Tooltip>
    );
  }
);
ExceptionalStatusMarker.displayName = "ExceptionalStatusMarker";
